import { getPersonalities, resolveCustomizerHcpTag } from "../../services/utils";

const filterByTag = (characterList: any[], hcpTag?: string) => {
  if (hcpTag) {
    return characterList.filter((character) => character.practitionerTag === hcpTag);
  }
  return characterList;
};

const withLastHCPAtIndexOne = (list: any[]) => {
  const lastHCPIndex = list.findIndex((c) => c.isLastHCP);
  if (lastHCPIndex >= 0 && list.length >= 2) {
    const without = [...list.slice(0, lastHCPIndex), ...list.slice(lastHCPIndex + 1)];
    return [without[0], list[lastHCPIndex], ...without.slice(1)];
  }
  return list;
};

export const getOrderedHCPList = (characterList: any[], hcpTag?: string) =>
  withLastHCPAtIndexOne(filterByTag(characterList, hcpTag));

/** Match a carousel/list row to the practitioner chosen in the customizer (`key` or `type`). */
export function characterMatchesPractitionerType(
  characterPractitionerType: string,
  selectedPractitionerType: string,
  practitionerTypes: { key?: string; type?: string }[] = [],
): boolean {
  if (!selectedPractitionerType) return false;
  if (characterPractitionerType === selectedPractitionerType) return true;
  const entry = practitionerTypes.find(
    (pt) => pt.key === selectedPractitionerType || pt.type === selectedPractitionerType,
  );
  if (!entry) return false;
  return (
    characterPractitionerType === entry.key || characterPractitionerType === entry.type
  );
}

export function findHCPListIndexForPractitionerType(
  characterList: any[],
  hcpTag: string | undefined,
  practitionerType: string,
  practitionerTypes: { key?: string; type?: string }[] = [],
): number {
  const list = getOrderedHCPList(characterList, hcpTag);
  return list.findIndex((c) =>
    characterMatchesPractitionerType(c.practitionerType, practitionerType, practitionerTypes),
  );
}

export function applyCustomizerToCharacterForm(
  prev: Record<string, unknown>,
  character: Record<string, unknown> | undefined,
  customizeForm: {
    practitionerType: string;
    objectionLevel: string;
    personalityType: string;
    beginTime?: number;
    topics?: string[];
    practitionerImage?: string;
  },
): Record<string, unknown> {
  const { isLastHCP: _last, ...characterBase } = character ?? {};
  return {
    ...prev,
    ...characterBase,
    personalityType: customizeForm.personalityType,
    objectionLevel: customizeForm.objectionLevel,
    topics: customizeForm.topics ?? [],
    practitionerType: customizeForm.practitionerType,
    ...(customizeForm.practitionerImage
      ? { practitionerImage: customizeForm.practitionerImage }
      : {}),
    ...(customizeForm.beginTime != null ? { beginTime: customizeForm.beginTime } : {}),
  };
}

export function clearPractitionerFields(prev: Record<string, unknown>) {
  const {
    practitionerType,
    practitionerImage,
    practitionerTag,
    objectionLevel,
    personalityType,
    hcpAvailableTime,
    topics: _topics,
    ...rest
  } = prev;
  return { ...rest, topics: [] };
}

export function formMatchesHcpTagFilter(form: Record<string, unknown>, hcpTag: string, list: any[]) {
  if (!form.practitionerType) return false;
  const tagMatches = !hcpTag || form.practitionerTag === hcpTag;
  const inFilteredList = list.some((c) => c.practitionerType === form.practitionerType);
  return tagMatches && inFilteredList;
}

/** True when the current form can safely be submitted to `startCall`. */
export function isPraxisPlayStartFormValid({
  form,
  hcpTag,
  characterList,
  tenantProfile,
  group,
}: {
  form: Record<string, unknown>;
  hcpTag: string;
  characterList: any[];
  tenantProfile: any;
  group?: string;
}): boolean {
  const filteredList = getOrderedHCPList(characterList, hcpTag);
  if (!formMatchesHcpTagFilter(form, hcpTag, filteredList)) {
    return false;
  }

  if (!form.personalityType || !form.objectionLevel) {
    return false;
  }

  const effectiveHcpTag = resolveCustomizerHcpTag(hcpTag);
  const personalitiesList = getPersonalities(
    tenantProfile?.personalities || {},
    group,
    effectiveHcpTag,
  );

  return personalitiesList.some((p) => p.key === form.personalityType);
}
